import { STATE } from "../classes/Enum";
import { getCartQuantity, toggleWish } from "../helper/main";
import { useAppState, useCart, useSearch } from "./Context";
import ItemImage from "./imgs/ItemImage";
import { useInViewClass } from "../helper/util";
import StockIndicator from "./StockDisplay";
import { useAuth } from "./AuthProvider";
import { canBuy, getListingPriceText } from "../helper/products";
import { getProductNameParts } from "../helper/productName";
import { ProductNameDisplay } from "./ProductName";
import { CartIcon } from "../icons/cart";
import { WishlistIcon } from "../icons/heart";
import { logItemSelect } from "../api/events";

const ConditionOverlay = ({ item }) => {
  if (!item?.conditionQuantities) return null;

  const availableConditions = Object.values(item.conditionQuantities)
    .flatMap(variant =>
      Object.entries(variant)
        .filter(([_, data]) => data.quantity > 0)
        .map(([condition]) => condition)
    );

  return (
    <div className="tool-condition-container">
      {[...new Set(availableConditions)].slice(0,3).map(condition => (
        <div className='tool-condition' key={condition}>
          {condition}
        </div>
      ))}
    </div>
  );
};

const WishButton = ({ isWished, item, setWishlist, onHoverEnd }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    toggleWish(isWished(item.id), setWishlist, item.id);
  };

  return (<div className={`collectdiv-outline`}>
    <div className="collectdiv-button">
      <button
        type="button"
        onClick={handleClick}
      >
        <WishlistIcon active={isWished(item.id)}/>
      </button>
    </div>
  </div>
  );
};


const AddToCartButton = ({addToCart, item, numInCart}) => {
    return <div className={`collectdiv-outline`}>
      <button
        className="collectdiv-button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          addToCart(item);
        }}
      >
        <CartIcon numInCart={numInCart}/>
      </button>
    </div>
}

function Product({query, item, count, setSelectedItem, state, onHoverStart, onHoverEnd, isHovered,
  hoveredId, overlayHeight, isWished, autoAddCart, setWishlist, isMobile, cardHeight, hideProductDetails = false}) {
    const {appState} = useAppState();
    const { searchEventId } = useSearch();
    const { ref, inView } = useInViewClass({ threshold: 0.82,   rootMargin: "0px 0px -25% 0px", });
    const oos = item.quantity <= 0;
    const showHoverInfo = (isMobile && inView);

    const openItemInfo = () => {
      setSelectedItem(item);
      if (item.type === STATE.CARD) {
        logItemSelect(item.id, query, searchEventId);
      }
    };

    function formatString(seriesName, setName) {
        if (seriesName && setName) {
            return seriesName + " | " + setName;
        }
        if (seriesName === null) {
            seriesName = "";
        }
        if (setName === null) {
            setName = "";
        }
        if (!isHovered)
        return seriesName + " " + setName;
    }

    function displayShortName(name) {
      const shortName = name;
      return shortName;
    }

    function displayShortSeriesSet(name) {
      const shortName = name.slice(0,22);

      return shortName;
    }

    function isNameScrollable(name) {
      return (name.length > 19) && (isHovered || showHoverInfo);
    }
    
    function isSubtextScrollable(name) {
      if (isMobile) {
        return (name.length > 25) && (isHovered || showHoverInfo);
      }
      return (name.length > 35) && isHovered;
    }

    function isNew(lastUpdated) {
      let lastUpdatedTime = new Date(lastUpdated).getTime();
      const oneWeek = 7 * 24 * 60 * 60 * 1000;
      return lastUpdatedTime && (Date.now() - lastUpdatedTime < oneWeek);
    }

    const { titleName, language } = getProductNameParts(item.name, item.type);
    const subtext = (item.type === STATE.CARD) ? formatString(item.seriesName, item.setName) : "";

    return (
      <div
      ref = {ref}
      className={`item-card
          ${isHovered ? "is-hovered" : ""}
          ${hoveredId && hoveredId !== item.id ? "is-not-hovered" : ""}
          ${isWished(item.id) ? "is-wished" : ""}
          ${hideProductDetails ? "item-card--no-details" : ""}
      `}
      onClick={openItemInfo}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      >
        <div className="item-card-media">
          { isNew(item.lastUpdated) && (
            <button className="ribbon" type="button">
              <span>New</span>
            </button>
          )}
          <ItemImage
            state={state}
            item={item}
            greyscale={oos}
            setSelectedItem={setSelectedItem}
            query={query}
            maxHeight={cardHeight - 50}
            disableSelection
          />
        </div>

          {state !== STATE.OVERVIEW &&
          <div
            className={`left-align product-overlay ${state.toLowerCase()}`}
            style={hideProductDetails ? undefined : { transform: `translateY(${overlayHeight}px)` }}
          >
            <>
              <div className={`${isNameScrollable(titleName) ? 'auto-scroll-line' : 'no-wrap'}`}>
                <ProductNameDisplay
                  displayName={titleName}
                  language={language}
                  className={`item-name ${(isHovered || showHoverInfo) ? 'is-hovered' : ''}`}
                  stacked
                />
              </div>
              <div className={`${isSubtextScrollable(subtext) ? 'auto-scroll-line' : 'no-wrap'}`}>
                <span className="set-series">{subtext}</span>
              </div>
              {!hideProductDetails && (
                <p>{item.subtext ? item.subtext.slice(0, 33) : ''}</p>
              )}
              {!hideProductDetails && <div className="item-divider" />}
            </>

              {!hideProductDetails && (
              <div className="product-details">
              <div className="left-align" style={{width: "55%"}}>
                  {item.quantity > 0 ?
                  <div className="product-availability-container">
                      <StockIndicator quantity={item.quantity} type={appState} priceText={getListingPriceText(item)} />
                      <ConditionOverlay item={item} />
                  </div> :
                  <div>
                      <div className="item-header">Out of stock</div>
                      <div className="item-header-transparent">None available</div>
                  </div>}
              </div>
              <div className="tool-container">
                  <WishButton toolName='wish' isWished={isWished} item={item} setWishlist={setWishlist} onHoverEnd={onHoverEnd}/>
                  {item.quantity > 0 &&
                  <div >
                  {canBuy(item, null, null) && <AddToCartButton addToCart={autoAddCart} item={item} numInCart={count}/>}
                  </div>
                  }
              </div>
              </div>
              )}
          </div>

          }
    </div>)
    
}

export default Product